import { useEffect, useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Copy, Check, Globe, Search, FileSearch, MousePointerClick, BadgeCheck, TrendingUp, Code2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const APP = 'https://app.adoptic.io';

function CopyBlock({ title, code, testId }) {
  const [ok, setOk] = useState(false);
  const copy = async () => { try { await navigator.clipboard.writeText(code); setOk(true); toast.success('Copied'); setTimeout(() => setOk(false), 1500); } catch { toast.error('Copy failed'); } };
  return (
    <div className="rounded-[14px] overflow-hidden border border-warm-border" data-testid={testId}>
      <div className="flex items-center justify-between px-3 py-2 bg-bg-warm border-b border-warm-border">
        <span className="font-body text-[11px] font-semibold uppercase tracking-wider text-ink-2">{title}</span>
        <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-orange text-white font-body text-[11px] font-semibold hover:bg-highlight-orange" data-testid={`${testId}-copy`}>{ok ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {ok ? 'Copied' : 'Copy'}</button>
      </div>
      <pre className="m-0 p-3 bg-ink text-[#F5F0E8] font-mono text-[11.5px] leading-relaxed overflow-x-auto whitespace-pre">{code}</pre>
    </div>
  );
}

const SNIPPET = `<div id="adoptic-bdaf"></div>\n<script src="${APP}/embed/bdaf-search.js" data-api="${APP}" async></script>`;
const API_DOCS = `# Only answered when the request Origin/Referer is adoptic.io, www.adoptic.io or app.adoptic.io (60 req/min per visitor)

# 1) Search by name, postcode or company number
GET ${APP}/api/public/bdaf/search?q=childcare&limit=8
GET ${APP}/api/public/bdaf/search?postcode=TS17&limit=8
→ { count, results: [{ id, company_name, company_number, postcode, postcode_district, local_authority,
     sector, bdaf_score, bdaf_tier, claimed, claim_status, audit_url, claim_url }] }

# 2) Full BDAF profile for one business (id or company number)
GET ${APP}/api/public/bdaf/business/{id}
→ { ...summary, signals:[{key,label,detected,action}], gaps:[...], district_rank, district_total, listings:[...] }

# 3) Track a landing-page event (widget does this automatically)
POST ${APP}/api/public/bdaf/event
Content-Type: application/json
{ "event": "search" | "audit_click" | "claim_click" | "widget_load",
  "session": "<visitor session id>", "query": "childcare", "result_count": 8, "business_id": "<id>" }

# Attribution: append ?src=widget to audit_url / claim_url so claims are counted as landing-page conversions`;
const FETCH_EXAMPLE = `const API = "${APP}";
const r = await fetch(\`\${API}/api/public/bdaf/search?q=\${encodeURIComponent(query)}&limit=8\`);
const { count, results } = await r.json();
results.forEach(b => console.log(b.company_name, b.bdaf_score, b.audit_url + "?src=widget", b.claim_url + "?src=widget"));

// optional: record your own events
navigator.sendBeacon(\`\${API}/api/public/bdaf/event\`,
  new Blob([JSON.stringify({ event: "search", query, result_count: count, session: mySessionId })], { type: "application/json" }));`;

export function WidgetAnalytics() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/widget-analytics?days=${days}`).then(r => setD(r.data)).catch(() => toast.error('Could not load widget analytics')); }, [days]);

  const kpis = d ? [
    { icon: Globe, label: 'Widget loads', value: d.totals.widget_load, sub: `${d.unique_sessions.widget_load} visitors`, tid: 'wa-kpi-loads' },
    { icon: Search, label: 'Searches', value: d.totals.search, sub: `${d.conversion.zero_result_rate}% no results`, tid: 'wa-kpi-searches' },
    { icon: FileSearch, label: 'Audit clicks', value: d.totals.audit_click, sub: `${d.funnel[2].rate}% of searches`, tid: 'wa-kpi-audits' },
    { icon: MousePointerClick, label: 'Claim clicks', value: d.totals.claim_click, sub: `${d.conversion.search_to_claim_click}% of searches`, tid: 'wa-kpi-claims' },
    { icon: BadgeCheck, label: 'Claims from landing', value: d.totals.claims_started, sub: `${d.totals.claims_verified} verified · ${d.totals.claims_pending} pending`, tid: 'wa-kpi-conversions' },
  ] : [];

  return (
    <div className="space-y-5" data-testid="widget-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-extrabold text-ink flex items-center gap-2"><TrendingUp className="w-4 h-4 text-brand-orange" /> Landing-page widget funnel</h2>
          <p className="font-body text-xs text-ink/50">adoptic.io search box → free audit → profile claim → verified owner</p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map(n => <button key={n} onClick={() => setDays(n)} data-testid={`wa-range-${n}`} className={`px-3 py-1 rounded-full font-body text-xs transition-colors ${days === n ? 'bg-ink text-white' : 'text-ink/50 hover:bg-ink/5'}`}>{n}d</button>)}
        </div>
      </div>

      {!d ? <div className="solid-card p-8 text-center font-body text-sm text-mid">Loading…</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {kpis.map(k => (
              <div key={k.label} className="solid-card p-4" data-testid={k.tid}>
                <k.icon className="w-4 h-4 text-brand-orange mb-2" strokeWidth={1.75} />
                <p className="font-display text-2xl font-extrabold text-ink">{k.value.toLocaleString()}</p>
                <p className="font-body text-xs font-semibold text-ink-2">{k.label}</p>
                <p className="font-body text-[11px] text-mid">{k.sub}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
            <div className="solid-card p-4">
              <p className="font-body text-xs font-semibold text-ink-2 mb-2">Daily activity</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={d.daily} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DC" />
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #E8E4DC', fontSize: 12 }} />
                    <Area type="monotone" dataKey="search" name="Searches" stroke="#1A1F2E" fill="#1A1F2E" fillOpacity={0.08} strokeWidth={2} />
                    <Area type="monotone" dataKey="audit_click" name="Audit clicks" stroke="#1A4D3A" fill="#1A4D3A" fillOpacity={0.12} strokeWidth={2} />
                    <Area type="monotone" dataKey="claim_click" name="Claim clicks" stroke="#FE4A2A" fill="#FE4A2A" fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="solid-card p-4" data-testid="wa-funnel">
              <p className="font-body text-xs font-semibold text-ink-2 mb-3">Conversion funnel</p>
              <div className="space-y-2">
                {d.funnel.map((f, i) => {
                  const max = d.funnel[0].n || 1;
                  return (
                    <div key={f.step}>
                      <div className="flex justify-between font-body text-xs"><span className="text-ink">{f.step}</span><span className="text-mid">{f.n.toLocaleString()} {i > 0 && <span className="text-ink-2 font-semibold">· {f.rate}%</span>}</span></div>
                      <div className="h-2 rounded-full bg-bg-warm mt-1 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.max(2, 100 * f.n / max)}%`, background: i >= 4 ? '#1A4D3A' : i >= 2 ? '#FE4A2A' : '#1A1F2E' }} /></div>
                    </div>
                  );
                })}
              </div>
              <p className="font-body text-[11px] text-mid mt-3">Load → verified owner: <b className="text-ink">{d.conversion.load_to_verified}%</b></p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="solid-card p-4" data-testid="wa-top-queries">
              <p className="font-body text-xs font-semibold text-ink-2 mb-2">Top searches</p>
              {d.top_queries.length === 0 ? <p className="font-body text-xs text-mid">No searches yet.</p> : (
                <table className="w-full font-body text-xs"><tbody>
                  {d.top_queries.map(q => <tr key={q.query} className="border-b border-warm-border last:border-0"><td className="py-1.5 text-ink">{q.query}</td><td className="py-1.5 text-right text-mid">{q.count}×</td><td className="py-1.5 text-right text-mid w-24">{q.avg_results} results</td></tr>)}
                </tbody></table>
              )}
            </div>
            <div className="solid-card p-4" data-testid="wa-top-businesses">
              <p className="font-body text-xs font-semibold text-ink-2 mb-2">Most clicked businesses</p>
              {d.top_businesses.length === 0 ? <p className="font-body text-xs text-mid">No clicks yet.</p> : (
                <table className="w-full font-body text-xs"><tbody>
                  {d.top_businesses.map(b => <tr key={b.business_id} className="border-b border-warm-border last:border-0"><td className="py-1.5 text-ink truncate max-w-[220px]">{b.company_name || b.business_id}</td><td className="py-1.5 text-mid">{b.postcode}</td><td className="py-1.5 text-right text-mid">{b.clicks} clicks</td><td className="py-1.5 text-right capitalize text-mid w-20">{b.claim_status || 'unclaimed'}</td></tr>)}
                </tbody></table>
              )}
            </div>
          </div>
        </>
      )}

      <div className="solid-card p-4 space-y-3" data-testid="wa-integration">
        <h3 className="font-display text-base font-extrabold text-ink flex items-center gap-2"><Code2 className="w-4 h-4 text-brand-orange" /> Copy to adoptic.io</h3>
        <CopyBlock title="Embed snippet (2 lines)" code={SNIPPET} testId="wa-snippet" />
        <CopyBlock title="Public BDAF API reference" code={API_DOCS} testId="wa-api-docs" />
        <CopyBlock title="Custom fetch example (if you build your own UI)" code={FETCH_EXAMPLE} testId="wa-fetch-example" />
        <p className="font-body text-[11px] text-mid">Live demo and preview-environment variant: <a href="/embed/index.html" target="_blank" rel="noreferrer" className="text-brand-orange underline">/embed/index.html</a></p>
      </div>
    </div>
  );
}
